import React from "react";
import { useNavigate } from "react-router";

import {
  Calendar,
  FlaskConical,
  MapPin,
  Music,
  Ticket,
  User,
  Users,
} from "lucide-react";
import moment from "moment";
import { z } from "zod";

import {
  EnrichmentAsyncHint,
  shouldSuppressEnrichmentLink,
} from "@/components/common/enrichment-async-hint";
import { UserAvatar } from "@/components/common/user-avatar";
import { EVENT_FEATURE_LABELS } from "@/components/enrichment/config";
import { EnrichmentSummary } from "@/components/enrichment/enrichment-summary";
import { PhotoWithBlurhash } from "@/components/media/photo-with-blurhash";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useMembers } from "@/contexts/member-context";
import { cn } from "@/lib/utils";
import { EventGroupLine } from "@/pages/events/components/event-group-line";
import { eventSchema } from "@/pages/events/edit";
import { OfferDialog } from "@/pages/events/features/offers-management/offer-dialog";

export default function EventSummary({
  data,
  children,
  justCreated = false,
  onGroupChanged,
  groupLocked = false,
}: {
  data: Partial<z.infer<typeof eventSchema>>;
  children?: React.ReactElement<any>[];
  /** `?created=1` off the post-create redirect. See `enrichment-async-hint`. */
  justCreated?: boolean;
  /** The event joined or left a group: the page re-reads it so the line and the form agree. */
  onGroupChanged?: () => void;
  /** The form has unsaved edits a re-read would drop, so the group line waits. */
  groupLocked?: boolean;
}) {
  const { members } = useMembers();
  const navigate = useNavigate();

  // `Unavailable` is the resting state of every Tina-created event until its
  // enrichment tail runs, so the "Enrich" CTA below is the resting CTA. This
  // exact affordance misleads — see `enrichment-async-hint`, which was first
  // scoped to geodata only because geodata was the sole synchronous create at
  // the time. The CTA is also the only route from here into
  // `/events/enrichment/:id`, which is why an `InProgress` no longer removes it.
  const hideEnrichmentLink = shouldSuppressEnrichmentLink(
    data.enrichmentStatus,
    justCreated,
  );

  const member = React.useMemo(
    () => members.find((x) => x.id === data.createdBy),
    [members],
  );

  const formatPrice = (price: number) => {
    return `€${price}`;
  };

  const availableTickets = data?.tickets?.filter(
    (ticket) => !ticket.dummy && ticket.name && ticket.price,
  );

  return (
    <Card className="w-full max-w-2xl">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <CardTitle className="text-xl font-bold">{data.name}</CardTitle>
            {/* <p className="text-sm text-muted-foreground">{data.about}</p> */}
          </div>
          <Badge variant="secondary" className="ml-2">
            {data.type || "Live"}
          </Badge>
        </div>
        {data.id &&
          (data.offers?.filter((o) => o.type === "giveaway")?.length ?? 0) >
            0 && (
            <OfferDialog
              eventId={data.id.toString()}
              offer={data.offers?.filter((o) => o.type === "giveaway")?.[0]}
            />
          )}
      </CardHeader>

      <CardContent className="space-y-4 px-8 pb-8">
        {data?.media?.photos && (
          <div className="flex flex-row space-x-2 overflow-auto">
            {data?.media?.photos.map((p) => {
              return (
                <PhotoWithBlurhash
                  key={p.url}
                  url={p.url}
                  blurhash={p.blurhash}
                />
              );
            })}
          </div>
        )}
        {/* Event Details */}
        <div className="flex flex-row justify-between">
          <div className="space-y-2 w-1/2">
            <div className="flex items-center space-x-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Start</span>
            </div>
            <div className="flex flex-wrap gap-2">
              <span className="text-sm">
                {moment(data?.startDate).format("DD/MM/YYYY HH:mm") ?? ""}
              </span>
            </div>
          </div>
          <div className="space-y-2 w-1/2">
            <div className="flex items-center space-x-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">End</span>
            </div>
            <div className="flex flex-wrap gap-2">
              <span className="text-sm">
                {moment(data?.endDate).format("DD/MM/YYYY HH:mm") ?? ""}
              </span>
            </div>
          </div>
        </div>
        <div className="space-y-2 w-1/2">
          <div className="flex items-center space-x-2">
            <MapPin className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Location</span>
          </div>
          <span className="text-sm">
            {[
              data.venueTba ? "Venue TBA" : data?.venue?.name,
              data.geodata &&
                `${data.geodata?.name} (${data.geodata?.countryCode})`,
            ]
              .filter(Boolean)
              .join(", ")}
          </span>
        </div>

        {/* Group — an event not saved yet has no id to add to a group */}
        {data.id && (
          <EventGroupLine
            eventId={data.id}
            eventLabel={data.name}
            group={data.group}
            onChanged={onGroupChanged}
            locked={groupLocked}
          />
        )}

        {/* Lineup */}
        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <Users className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Lineup</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {data?.lineup?.map((artist, index) => (
              <Badge key={index} variant="outline">
                {artist.name}
              </Badge>
            ))}
          </div>
        </div>

        {/* Genres */}
        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <Music className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Genres</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {data?.genres?.map((genre, index) => (
              <Badge key={index} variant="secondary">
                {genre.name}
              </Badge>
            ))}
          </div>
        </div>

        {/* Ticketing Information */}
        {data.ticketing && (
          <>
            <Separator />
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Ticket className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Ticketing</span>
                </div>
              </div>
              <div className="space-y-2">
                <span className="text-sm font-medium">Provider</span>

                <div className="flex items-center space-x-2">
                  <img
                    src={
                      data?.ticketing?.providerMetadata?.logo ||
                      "/placeholder.svg"
                    }
                    alt={data.ticketing.provider}
                    className="w-24"
                  />
                  <span className="text-xs text-muted-foreground">
                    {data.ticketing.provider}
                  </span>
                </div>
              </div>

              {/* Form Status */}
              <div className="text-xs text-muted-foreground">
                {data.ticketing.form ? "Form required" : "No form required"}
              </div>

              {/* Available Tickets */}
              {availableTickets && availableTickets.length > 0 && (
                <div className="space-y-2">
                  <span className="text-sm font-medium">Available Tickets</span>
                  <div className="space-y-2">
                    {availableTickets?.map((ticket, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-2 bg-muted/50 rounded-md"
                      >
                        <div className="space-y-1">
                          <div className="text-sm font-medium">
                            {ticket.name}
                          </div>
                          {ticket.quantity && (
                            <div className="text-xs text-muted-foreground">
                              {ticket.quantity} available
                            </div>
                          )}
                        </div>
                        {ticket.price && (
                          <div className="text-sm font-bold">
                            {formatPrice(ticket.price)}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Buy Tickets Button */}
              <Button
                className="w-full"
                onClick={() => window.open(data?.ticketing?.url, "_blank")}
              >
                Buy Tickets
              </Button>
            </div>
          </>
        )}
        {/* AI Enrichment */}
        {(data.enrichmentStatus || justCreated) && (
          <>
            <Separator />
            <div className="space-y-3">
              <EnrichmentSummary
                resource="events"
                id={String(data.id ?? "")}
                status={data.enrichmentStatus}
                enrichment={data.enrichment}
                featureLabels={EVENT_FEATURE_LABELS}
                showLink={false}
              />
              <EnrichmentAsyncHint
                status={data.enrichmentStatus}
                justCreated={justCreated}
              />

              {!hideEnrichmentLink && (
                <Button
                  size="sm"
                  variant={
                    data.enrichmentStatus === "NeedsReview"
                      ? "default"
                      : "outline"
                  }
                  className={cn(
                    "w-full gap-1.5",
                    data.enrichmentStatus === "NeedsReview" && "animate-pulse",
                  )}
                  onClick={() => navigate(`/events/enrichment/${data.id}`)}
                >
                  <FlaskConical className="h-3.5 w-3.5" />
                  {data.enrichmentStatus === "NeedsReview"
                    ? "Review Enrichment"
                    : !data.enrichmentStatus ||
                        data.enrichmentStatus === "Unavailable"
                      ? "Enrich"
                      : "View Enrichment"}
                </Button>
              )}
            </div>
          </>
        )}

        <div className="space-y-2 w-1/2">
          <div className="flex items-center space-x-2">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Created At</span>
          </div>
          <div className="flex flex-wrap gap-2">
            <span className="text-sm">
              {moment(data?.createdAt).format("DD/MM/YYYY HH:mm") ?? ""}
            </span>
          </div>
        </div>
        <div className="space-y-2 w-1/2">
          <div className="flex items-center space-x-2">
            <User className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Created By</span>
          </div>
          <div className="flex flex-wrap gap-2">
            <UserAvatar {...member} half />
          </div>
        </div>
      </CardContent>
      <CardFooter>{children}</CardFooter>
    </Card>
  );
}
